<template>
  <Avatar ref="avatarRef" :src="src" :name="name" :alt="t('accessibility.clientAvatar')" :size="size" :variant="variant" />
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import Avatar from '@/components/atoms/Avatar.vue'
import { useClientAvatar } from '@/composables/useClientAvatar'

interface Props {
  clientId: string
  /** The cliënt's `avatarUrl`; only whether it is set is read, the photo is fetched authenticated. */
  avatarUrl?: string | null
  name: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'default' | 'subtle'
  /** Fetch only once the avatar scrolls into view — for long lists. */
  lazy?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  avatarUrl: null,
  size: 'md',
  variant: 'default',
  lazy: false
})

const { t } = useI18n()

const avatarRef = ref<InstanceType<typeof Avatar> | null>(null)
const visible = ref(!props.lazy)

if (props.lazy) {
  const { stop } = useIntersectionObserver(() => avatarRef.value?.$el as HTMLElement | undefined, ([entry]) => {
    if (!entry?.isIntersecting) return
    visible.value = true
    stop()
  })
}

const { src } = useClientAvatar(() => props.clientId, () => props.avatarUrl, visible)
</script>
